import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import { useAuth } from 'context/AuthContext';
import useEscapeClose from 'hooks/useEscapeClose';

const EMPTY = { code: '', designation: '', description: '', unite: 'unite', prix_unitaire_ht: '', prix_achat: '', categorie: '', stock_disponible: 0, seuil_alerte: 5, vendable_tiers: true, usage_interne: true };

const CAT_COLORS = {
  'Equipement agent': '#8B1A1A',
  'Videosurveillance': '#1565c0',
  'Alarme et detection': '#e65100',
  'Controle acces': '#6a1b9a',
  'Detection incendie': '#c62828',
  'Securite perimetrique': '#2e7d32',
  'Signaletique': '#f57c00',
  'Formation et conseil': '#00838f',
  'Telesurveillance': '#37474f',
};

export default function CataloguePage() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some(r => ['admin', 'dg'].includes(r));
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [error, setError] = useState('');

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/catalogue');
      setProduits(res.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      code: p.code, designation: p.designation, description: p.description || '',
      unite: p.unite || 'unite', prix_unitaire_ht: p.prix_unitaire_ht, prix_achat: p.prix_achat || '',
      categorie: p.categorie || '', stock_disponible: p.stock_disponible || 0,
      seuil_alerte: p.seuil_alerte || 5, vendable_tiers: p.vendable_tiers, usage_interne: p.usage_interne,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing) { await api.put(`/catalogue/${editing}`, form); }
      else { await api.post('/catalogue', form); }
      setShowForm(false);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try { await api.delete(`/catalogue/${id}`); load(); } catch {}
  };

  const categories = [...new Set(produits.filter(p => p.categorie).map(p => p.categorie))].sort();
  const filtered = produits.filter(p => {
    if (catFilter && p.categorie !== catFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return p.code.toLowerCase().includes(s) || p.designation.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div>
      {/* KPIs */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Produits</span></div>
          <div className="stat-card-value">{produits.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Categories</span></div>
          <div className="stat-card-value">{categories.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Actifs</span></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{produits.filter(p => p.actif).length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Vendables tiers</span></div>
          <div className="stat-card-value">{produits.filter(p => p.vendable_tiers).length}</div>
        </div>
      </div>

      {error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}

      {/* Toolbar */}
      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Catalogue produits</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="gss-input" placeholder="Rechercher par nom ou ref..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
            {isAdmin && <button className="gss-btn gss-btn-primary" onClick={openNew}>+ Nouveau produit</button>}
          </div>
        </div>

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          <button className={`gss-btn ${!catFilter ? 'gss-btn-primary' : ''}`} onClick={() => setCatFilter('')} style={{ fontSize: 12 }}>
            Toutes ({produits.length})
          </button>
          {categories.map(c => {
            const count = produits.filter(p => p.categorie === c).length;
            return (
              <button key={c} className={`gss-btn ${catFilter === c ? 'gss-btn-primary' : ''}`} onClick={() => setCatFilter(c)} style={{ fontSize: 12 }}>
                {c} ({count})
              </button>
            );
          })}
        </div>

        {/* Product cards grid */}
        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDD" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginTop: 16 }}>Aucun produit</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {filtered.map(p => {
              const prix = Number(p.prix_unitaire_ht);
              const achat = Number(p.prix_achat) || 0;
              const marge = achat > 0 ? prix - achat : 0;
              const catColor = CAT_COLORS[p.categorie] || '#888';
              return (
                <div key={p.id} style={{ border: '1px solid #EEE', borderRadius: 10, padding: 16, background: '#FAFAFA', position: 'relative' }}>
                  {/* Category badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: catColor, background: catColor + '15', padding: '2px 8px', borderRadius: 4 }}>
                      {p.categorie || '-'}
                    </span>
                    <span style={{ fontSize: 10, color: '#888' }}>{p.code}</span>
                  </div>
                  {/* Name */}
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{p.designation}</div>
                  {p.description && <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>{p.description}</div>}
                  {/* Price */}
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--gss-red)' }}>{prix.toLocaleString()} <span style={{ fontSize: 12 }}>FCFA</span></div>
                  <div style={{ fontSize: 10, color: '#999' }}>/{p.unite}</div>
                  {/* Marge (admin only) */}
                  {isAdmin && achat > 0 && (
                    <div style={{ fontSize: 11, color: '#2e7d32', marginTop: 4 }}>
                      Marge : {marge.toLocaleString()} F ({Math.round(marge / prix * 100)}%)
                    </div>
                  )}
                  {/* Stock */}
                  {p.stock_disponible > 0 && (
                    <div style={{ fontSize: 11, color: p.stock_disponible < (p.seuil_alerte || 5) ? '#DC2626' : '#888', marginTop: 4 }}>
                      Stock : {p.stock_disponible}
                    </div>
                  )}
                  {/* Actions */}
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                      <button className="gss-btn-sm" onClick={() => openEdit(p)}>Modifier</button>
                      <button className="gss-btn-sm" style={{ color: '#DC2626' }} onClick={() => handleDelete(p.id)}>Supprimer</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>{editing ? 'Modifier le produit' : 'Nouveau produit'}</h2>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field"><label>Reference *</label><input className="gss-input" required value={form.code} onChange={e => set('code', e.target.value)} placeholder="GLD-XX-001" /></div>
                  <div className="gss-field"><label>Designation *</label><input className="gss-input" required value={form.designation} onChange={e => set('designation', e.target.value)} /></div>
                  <div className="gss-field"><label>Categorie</label>
                    <select className="gss-input" value={form.categorie} onChange={e => set('categorie', e.target.value)}>
                      <option value="">-- Choisir --</option>
                      {Object.keys(CAT_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Unite</label>
                    <select className="gss-input" value={form.unite} onChange={e => set('unite', e.target.value)}>
                      <option value="unite">Unite</option><option value="lot">Lot</option><option value="mois">Mois</option>
                      <option value="forfait">Forfait</option><option value="session">Session</option><option value="journee">Journee</option>
                      <option value="forfait/an">Forfait/an</option><option value="intervention">Intervention</option><option value="ml">Metre lineaire</option>
                    </select>
                  </div>
                  <div className="gss-field"><label>Prix achat (FCFA)</label><input className="gss-input" type="number" min="0" value={form.prix_achat} onChange={e => set('prix_achat', e.target.value)} /></div>
                  <div className="gss-field"><label>Prix vente (FCFA) *</label><input className="gss-input" type="number" required min="0" value={form.prix_unitaire_ht} onChange={e => set('prix_unitaire_ht', e.target.value)} /></div>
                  <div className="gss-field"><label>Stock disponible</label><input className="gss-input" type="number" min="0" value={form.stock_disponible} onChange={e => set('stock_disponible', e.target.value)} /></div>
                  <div className="gss-field"><label>Seuil alerte</label><input className="gss-input" type="number" min="0" value={form.seuil_alerte} onChange={e => set('seuil_alerte', e.target.value)} /></div>
                  <div className="gss-field gss-field-full"><label>Description</label><textarea className="gss-input" rows="2" value={form.description} onChange={e => set('description', e.target.value)} style={{ resize: 'vertical' }} /></div>
                  <div className="gss-field"><label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.vendable_tiers} onChange={e => set('vendable_tiers', e.target.checked)} /> Vendable tiers</label></div>
                  <div className="gss-field"><label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.usage_interne} onChange={e => set('usage_interne', e.target.checked)} /> Usage interne</label></div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy">{editing ? 'Enregistrer' : 'Creer le produit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
